import { isEmpty, reverse, sortBy } from 'lodash-es';
import React from 'react';
import LockIcon from '@/assets/images/menu/lock-on.react.svg?react';
import { MultiApp } from '@/config/context';
import i18n from '@/i18n';
import { ENV } from '@/infrastructure/env-config';
import { getCapitalPlatform, isAndroid } from '@/infrastructure/product-info';
import { CapitalPlatform, Platform } from '@/infrastructure/utils/app';
import { ProductType } from '@/service/product-permissions';
import { stores } from '../store';
import { RawPerm } from '../store/product';
import { alertRedirect } from './entries/alert-redirect';
import { anrRoutes } from './entries/anr';
import { anrHarmonyRoutes } from './entries/anr-hormony';
import { productApprovalRoutes } from './entries/approval';
import { complianceRoutes } from './entries/compliance';
import { configManageRoutes } from './entries/config-manage';
import { cupRoutes } from './entries/cpu';
import { crashRoutes } from './entries/crash';
import { diagnoseRoutes } from './entries/diagnose';
import { diskRoutes } from './entries/disk';
import { dropFrameRoutes } from './entries/drop-frame';
import { pageDurationRoutes } from './entries/duration';
import { electronRoutes } from './entries/electron';
import { electronCrashRoutes } from './entries/electron-crash';
import { errorRoutes } from './entries/error';
import { expenseRoutes } from './entries/expense';
import { foomRoutes } from './entries/foom';
import { gamePerformanceRoutes } from './entries/game-performance';
import { hippyRoutes } from './entries/hippy';
import { kuiklyRoutes } from './entries/kuikly';
import { launchRoutes } from './entries/launch';
import { llmObservabilityRoutes } from './entries/llm-observability';
import { memoryRoutes } from './entries/memory';
import { metricKitRoutes } from './entries/metric-kit';
import { miniProgramRoutes } from './entries/miniprogram';
import { netRoutes } from './entries/net';
import foomRoutesIos from './entries/oom';
import { overviewRoutes } from './entries/overview';
import { powerRoutes } from './entries/power';
import { productRoutes } from './entries/product';
import { reactNativeRoutes } from './entries/react-native';
import { redirectRoute } from './entries/redirect';
import { resourceManageRoutes } from './entries/resource-manage';
import { resourceRoutes } from './entries/resource-pack';
import { schemaFormPlaygroundRoutes } from './entries/schema-form-playground';
import { settingRoutes } from './entries/setting';
import { trackRoutes } from './entries/track';
import { trafficRoutes } from './entries/traffic';
import { trialRoutes } from './entries/trial';
import { usageDetailRoutes } from './entries/usage-details';
import { userRoutes } from './entries/user';
import { webRoutes } from './entries/web';
import { entryRoute } from './entry';
import { rightlyRoutes } from './overlays/rightly';
import { shiplyRoutes } from './overlays/shiply';
import { IRouteConfig } from './types';

/**
 * 路由表
 * 参数说明：
 * @hideInMenu 侧边栏入口是否隐藏，默认 false
 * @disabled 是否禁用，默认 false
 * @whitelisted 白名单，默认 false
 * @enabledMultiApps 哪个版本展示 string[]
 * @latforms 哪个平台展示
 * @excludePatforms 哪个平台不展示
 * @versions 哪个版本展示
 */
const deepFilterRoutes = (routes: IRouteConfig[]): IRouteConfig[] => {
  const platform = getCapitalPlatform() as CapitalPlatform;
  const version = ENV.currentAppVersion as MultiApp;

  const { productType } = stores.ProductStore as typeof stores.ProductStore & { productType?: ProductType };

  const res = routes.map((route, index) => {
    // 没配sort的 按写的顺序排, 因为用到了翻转，所以排前面的值越小
    const sort = route.sort ? route.sort : (routes.length - index);
    const originalName = route.name;
    const name = typeof originalName === 'string' ? i18n.t(originalName) : originalName;
    let extra = (productType === ProductType.FREE_PRODUCT && route.disabledByFree) ? <LockIcon /> : undefined;
    if (originalName === 'ANR' && !isAndroid() && productType === ProductType.FREE_PRODUCT) {
      extra = <LockIcon />;
    }
    if (route.routes) {
      return {
        ...route,
        name,
        sort,
        extra,
        routes: deepFilterRoutes(route.routes),
      };
    }

    return { ...route, name, sort, extra };
  }).filter((route) => {
    const { platforms = [], versions = [], excludePlatforms = [], validator } = route;

    // 会员权限校验
    // const authed = !authName || authorityNodes.includes(authName);
    // const authed = true;

    const matchExcludePlatforms = isEmpty(excludePlatforms) || !excludePlatforms.includes(platform);
    const matchPlatforms = isEmpty(platforms) || platforms.includes(platform);
    const matchVersion = isEmpty(versions) || versions.includes(version);
    const validate = !validator || validator(route);

    return matchExcludePlatforms && matchPlatforms && matchVersion && validate;
    // return authed && matchExcludePlatforms && matchPlatforms && matchVersion && validate;
  });

  return reverse(sortBy(res, 'sort'));
};

const PRO_VERSIONS = [MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu];

const isProVersion = () => PRO_VERSIONS.includes(ENV.currentAppVersion as MultiApp);

const getProStandaloneRoutes = (): IRouteConfig[] => {
  if (!isProVersion()) {
    return [];
  }

  return [
    ...productApprovalRoutes(),
    ...resourceManageRoutes(),
    ...resourceRoutes(),
    ...expenseRoutes(),
    ...trialRoutes(),
    ...usageDetailRoutes(),
  ];
};

const withProStandaloneRoutes = (routes: IRouteConfig[]): IRouteConfig[] => [
  ...routes,
  ...getProStandaloneRoutes(),
];

const getElectronRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...electronCrashRoutes(),
  ...electronRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getReactNativeRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...reactNativeRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getMiniProgramRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...miniProgramRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getWindowsRoutes = (cpuEnable: boolean, rawPerm: RawPerm): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...crashRoutes(),
  ...anrRoutes(),
  ...errorRoutes(),
  ...gamePerformanceRoutes(),
  ...dropFrameRoutes(),
  ...memoryRoutes(),
  ...netRoutes(),
  ...trackRoutes(),
  ...cupRoutes(cpuEnable),
  ...diagnoseRoutes(rawPerm?.isAdmin, stores.ProductStore.product?.diagnoseGuidMappingField),
  ...settingRoutes(),
  ...productRoutes(),
  ...configManageRoutes(),
  ...userRoutes(),
]);

const getKuiklyRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...kuiklyRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getHippyRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...hippyRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getHarmonyOSRoutes = (rawPerm: RawPerm): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...crashRoutes(),
  ...anrRoutes(),
  ...anrHarmonyRoutes(),
  ...errorRoutes(),
  ...dropFrameRoutes(),
  ...memoryRoutes(),
  ...trackRoutes(),
  ...diagnoseRoutes(rawPerm?.isAdmin, stores.ProductStore.product?.diagnoseGuidMappingField),
  ...complianceRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...configManageRoutes(),
  ...entryRoute(),
  ...userRoutes(),
]);

const getFlutterRoutes = (rawPerm: RawPerm): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...errorRoutes(),
  ...dropFrameRoutes(),
  ...launchRoutes(),
  ...netRoutes(),
  ...trackRoutes(),
  ...diagnoseRoutes(rawPerm?.isAdmin, stores.ProductStore.product?.diagnoseGuidMappingField),
  ...settingRoutes(),
  ...productRoutes(),
  ...configManageRoutes(),
  ...entryRoute(),
  ...userRoutes(),
]);

const getWebRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...webRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...userRoutes(),
]);

const getLinuxRoutes = (): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...crashRoutes(),
  ...errorRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...configManageRoutes(),
  ...entryRoute(),
  ...userRoutes(),
]);

const getDefaultRoutes = (
  cpuEnable: boolean,
  rawPerm: RawPerm,
): IRouteConfig[] => withProStandaloneRoutes([
  ...overviewRoutes(),
  ...alertRedirect(),
  ...crashRoutes(),
  ...anrRoutes(),
  ...anrHarmonyRoutes(),
  ...foomRoutes(),
  ...foomRoutesIos(),
  ...errorRoutes(),
  ...dropFrameRoutes(),
  ...cupRoutes(cpuEnable),
  ...memoryRoutes(),
  ...launchRoutes(),
  ...netRoutes(),
  ...trafficRoutes(),
  ...powerRoutes(),
  ...diskRoutes(),
  ...gamePerformanceRoutes(),
  ...trackRoutes(),
  ...metricKitRoutes(),
  ...complianceRoutes(),
  ...diagnoseRoutes(rawPerm?.isAdmin, stores.ProductStore.product?.diagnoseGuidMappingField),
  ...pageDurationRoutes(),
  ...llmObservabilityRoutes(),
  ...settingRoutes(),
  ...productRoutes(),
  ...configManageRoutes(),
  ...entryRoute(),
  ...schemaFormPlaygroundRoutes(),
  ...userRoutes(),
]);

const getEntryRouteList = (cpuEnable = false): IRouteConfig[] => {
  const { pid, rawPerm } = stores.ProductStore;

  const platformRoutesMap: Record<string, () => IRouteConfig[]> = {
    [Platform.Electron]: () => getElectronRoutes(),
    [Platform.ReactNative]: () => getReactNativeRoutes(),
    [Platform.MiniProgram]: () => getMiniProgramRoutes(),
    [Platform.Windows]: () => getWindowsRoutes(cpuEnable, rawPerm),
    [Platform.Kuikly]: () => getKuiklyRoutes(),
    [Platform.Hippy]: () => getHippyRoutes(),
    [Platform.HarmonyOS]: () => getHarmonyOSRoutes(rawPerm),
    [Platform.Flutter]: () => getFlutterRoutes(rawPerm),
    [Platform.Linux]: () => getLinuxRoutes(),
    [Platform.WEB]: () => getWebRoutes(),
  };

  const routeGenerator = platformRoutesMap[pid];
  const entryRoutes = routeGenerator ? routeGenerator() : getDefaultRoutes(cpuEnable, rawPerm);

  return [
    ...entryRoutes,
    ...redirectRoute(),
  ];
};

const getOverlayRouteList = (): IRouteConfig[] => [
  ...rightlyRoutes(),
  ...shiplyRoutes(),
];

export const getRoutes = (cpuEnable = false) => deepFilterRoutes([
  ...getEntryRouteList(cpuEnable),
  ...getOverlayRouteList(),
]);

export const getEntriesRoutes = (cpuEnable = false) => deepFilterRoutes(getEntryRouteList(cpuEnable));
export const getOverlaysRoutes = () => deepFilterRoutes(getOverlayRouteList());
